import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useRoles } from "@/hooks/useRoles";
import { IconProps } from "@/interfaces/IconProps";

/**
 * Internal interface extending IconProps with additional tooltip-specific properties.
 * This interface is only used within the IconWithTooltip component.
 */
interface IconWithTooltipProps extends IconProps {
  tooltip?: string;
  onClick?: () => void;
  isDefaultValue?: boolean;
}

/**
 * IconWithTooltip Component
 *
 * Renders an icon with role-based state and a portal-rendered tooltip.
 * Supports two icon types: "edit" and "reset" with conditional enabling/disabling
 * based on user role and data state.
 *
 * Features:
 * - Role-based icon rendering (admin vs non-admin)
 * - Dynamic tooltips with mouse tracking
 * - Portal rendering for proper z-index layering in AG Grid
 * - Keyboard accessibility support
 *
 * @param iconName - Type of icon to render ("edit" or "reset")
 * @param size - Icon dimensions in pixels
 * @param className
 * @param style
 * @param alt
 * @param tooltip
 * @param isDefaultValue - Whether the value is already set to default (affects reset icon state)
 * @param onClick - Click handler (only fires when icon is enabled)
 */
export const IconWithTooltip: React.FC<IconWithTooltipProps> = ({
  iconName = "Edit",
  size = 20,
  className,
  style,
  alt = "Icon",
  tooltip,
  onClick,
  isDefaultValue = false,
}) => {
  const { isAdmin } = useRoles();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const wrapperRef = useRef<HTMLSpanElement>(null);

  /**
   * Determines icon state based on icon type and user role.
   * Returns the appropriate icon source, tooltip text, and clickability.
   */
  const getIconState = () => {
    const name = (iconName || "").toLowerCase();

    // Edit icon: enabled for admins only
    if (name === "edit") {
      return {
        src: isAdmin
          ? "/images/svg/pencil-blue.svg"
          : "/images/svg/pencil-grey.svg",
        tooltip: isAdmin ? "Edit" : "Edit only available for Admins",
        isClickable: isAdmin,
      };
    }

    // Reset icon: disabled if not admin OR value is already default
    if (name === "reset") {
      const isDisabled = !isAdmin || isDefaultValue;
      const src = isDisabled
        ? "/images/svg/reset-in-circle-grey.svg"
        : "/images/svg/reset-in-circle-blue.svg";

      // Determine tooltip message based on state
      let tooltipText = "Set to default";
      if (!isAdmin) {
        tooltipText = "Reset to default only available for Admins";
      } else if (isDefaultValue) {
        tooltipText = "Already set to default";
      }

      return {
        src,
        tooltip: tooltipText,
        isClickable: isAdmin && !isDefaultValue,
      };
    }

    // Fallback for unknown icon types
    return {
      src: "/images/svg/pencil-grey.svg",
      tooltip: tooltip || "",
      isClickable: false,
    };
  };

  const iconState = getIconState();

  /**
   * Updates tooltip position to follow mouse cursor.
   * Positions tooltip 30px above cursor with horizontal centering.
   */
  const handleMouseMove = (e: React.MouseEvent) => {
    if (wrapperRef.current) {
      setPosition({
        x: e.clientX,
        y: e.clientY - 30,
      });
    }
  };

  // Utility function to convert number to pixel string
  const toPx = (v: number | string) => (typeof v === "number" ? `${v}px` : v);

  // Icon wrapper styling
  const wrapperStyle: React.CSSProperties = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    cursor: iconState.isClickable ? "pointer" : "default",
    width: toPx(size),
    height: toPx(size),
    ...style,
  };

  // Tooltip styling with fixed positioning for portal rendering
  const tooltipStyle: React.CSSProperties = {
    position: "fixed",
    top: position.y,
    left: position.x,
    transform: "translateX(-50%)",
    background: "rgba(0, 0, 0, 0.85)",
    color: "#fff",
    padding: "6px 10px",
    borderRadius: 4,
    fontSize: 13,
    whiteSpace: "nowrap",
    pointerEvents: "none",
    zIndex: 9999, // Ensures tooltip appears above AG Grid cells
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
    opacity: visible ? 1 : 0,
    transition: "opacity 150ms ease-in-out",
  };

  return (
    <>
      {/* Icon wrapper with accessibility attributes */}
      <span
        ref={wrapperRef}
        role={iconState.isClickable ? "button" : undefined}
        tabIndex={iconState.isClickable ? 0 : undefined}
        aria-label={alt}
        aria-disabled={!iconState.isClickable}
        style={wrapperStyle}
        className={className}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onMouseMove={handleMouseMove}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        onClick={iconState.isClickable && onClick ? onClick : undefined}
      >
        <img
          src={iconState.src}
          alt={alt}
          style={{
            width: toPx(size),   // Set width based on size prop
            height: toPx(size),
            display: "block",
            opacity: iconState.isClickable ? 1 : 0.5, // Dimmed when disabled
          }}
        />
      </span>
      {/* Portal-rendered tooltip attached to document.body to bypass AG Grid overflow */}
      {visible &&
        createPortal(
          <span style={tooltipStyle} aria-hidden={!visible}>
            {iconState.tooltip}
          </span>,
          document.body
        )}
    </>
  );
};

/**
 * Icon Component
 * 
 * Simple wrapper around IconWithTooltip for backward compatibility.
 * Can be used when tooltip functionality is not needed.
 */
export const Icon: React.FC<IconProps> = (props) => {
  return <IconWithTooltip {...props} />;
};

export default Icon;